import { addDays, differenceInDays, getDay, getHours, getMinutes, format } from 'date-fns';

/**
 * Get wall clock time (local time) ignoring DST transitions
 * This ensures 11:00 AM stays 11:00 AM regardless of DST changes
 */
const getWallClockTime = (date) => {
  return {
    hours: getHours(date),
    minutes: getMinutes(date)
  };
};

/**
 * Calculate time difference in minutes between two wall clock times
 */
const getWallClockTimeDifference = (date1, targetHours, targetMinutes) => {
  const time1 = getWallClockTime(date1);
  return Math.abs(time1.hours * 60 + time1.minutes - (targetHours * 60 + targetMinutes));
};

/**
 * Auto-fill remaining lessons based on first selection
 * @param {string} firstSelection - ISO timestamp of first selected lesson
 * @param {Array} remainingLessons - Array of lesson objects that need scheduling
 * @param {Array} availableSlots - Array of available timestamp strings
 * @param {Object} monthlyQuotas - Object with monthly session quotas (optional)
 * @param {Array} existingBookings - Array of existing booking timestamps (optional)
 * @returns {Array} Array of suggestions with { timestamp, isAlternative, lessonId }
 */
export const autoFillSchedule = (firstSelection, remainingLessons, availableSlots, monthlyQuotas = null, existingBookings = []) => {
  if (!remainingLessons.length || !availableSlots.length) return [];

  const firstDate = new Date(firstSelection);
  const targetDayOfWeek = getDay(firstDate);
  const targetHour = getHours(firstDate);
  const targetMinute = getMinutes(firstDate);

  const suggestions = [];
  const usedSlots = new Set([firstSelection]); // Track used slots including first selection

  // Group available slots by month for quota management and track existing bookings
  const slotsByMonth = groupSlotsByMonth(availableSlots, existingBookings);

  let lastScheduledDate = firstDate;

  for (let i = 0; i < remainingLessons.length; i++) {
    // Calculate ideal spacing based on the month we're scheduling in
    const currentMonth = new Date(lastScheduledDate);
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const dayOfMonth = currentMonth.getDate();
    const remainingDaysInMonth = daysInMonth - dayOfMonth;

    // Get sessions available for this month (default to 2 if not specified)
    const sessionsAvailableInMonth = monthlyQuotas?.currentMonth?.total || 2;

    // Calculate ideal spacing for this specific month
    const idealSpacing = Math.floor(daysInMonth / sessionsAvailableInMonth);

    // Start with ideal spacing, but may need to adjust based on remaining days
    let targetSpacing = Math.min(idealSpacing, remainingDaysInMonth);

    // Calculate target date for this lesson
    const targetDate = addDays(lastScheduledDate, targetSpacing);

    const suggestion = findBestMatchWithQuotas(
      targetDate,
      targetDayOfWeek,
      targetHour,
      targetMinute,
      availableSlots,
      usedSlots,
      slotsByMonth,
      monthlyQuotas,
      firstDate
    );

    if (suggestion) {
      suggestions.push({
        ...suggestion,
        lessonId: remainingLessons[i].id
      });

      usedSlots.add(suggestion.timestamp);
      lastScheduledDate = new Date(suggestion.timestamp);

      // Update monthly quota tracking
      if (monthlyQuotas) {
        const monthKey = format(new Date(suggestion.timestamp), 'yyyy-MM');
        if (slotsByMonth[monthKey]) {
          slotsByMonth[monthKey].used = (slotsByMonth[monthKey].used || 0) + 1;
        }
      }
    } else {
      break; // Stop scheduling if we can't find a match
    }
  }

  return suggestions;
};

/**
 * Group available slots by month for quota tracking
 */
const groupSlotsByMonth = (availableSlots, existingBookings = []) => {
  const slotsByMonth = {};

  availableSlots.forEach((slot) => {
    const monthKey = format(new Date(slot), 'yyyy-MM');
    if (!slotsByMonth[monthKey]) {
      slotsByMonth[monthKey] = {
        slots: [],
        used: 0
      };
    }
    slotsByMonth[monthKey].slots.push(slot);
  });

  // Count existing bookings per month
  existingBookings.forEach((booking) => {
    const monthKey = format(new Date(booking), 'yyyy-MM');
    if (!slotsByMonth[monthKey]) {
      slotsByMonth[monthKey] = {
        slots: [],
        used: 0
      };
    }
    slotsByMonth[monthKey].used += 1;
  });

  return slotsByMonth;
};

/**
 * Find best matching time slot with quota awareness
 */
const findBestMatchWithQuotas = (
  targetDate,
  targetDayOfWeek,
  targetHour,
  targetMinute,
  availableSlots,
  usedSlots,
  slotsByMonth,
  monthlyQuotas,
  firstSelection
) => {
  const preferences = [
    // Level 1: Exact match (same weekday, same wall clock time)
    {
      name: 'exact',
      filter: (slot) => {
        const slotDate = new Date(slot);
        const slotDay = getDay(slotDate);
        const slotHour = getHours(slotDate);
        const slotMinute = getMinutes(slotDate);

        return slotDay === targetDayOfWeek && slotHour === targetHour && slotMinute === targetMinute;
      },
      isAlternative: false
    },

    // Level 2: Same weekday, adjacent wall clock time (±30min, ±1hr)
    {
      name: 'adjacent-time',
      filter: (slot) => {
        const slotDate = new Date(slot);
        const timeDiff = getWallClockTimeDifference(slotDate, targetHour, targetMinute);
        return getDay(slotDate) === targetDayOfWeek && timeDiff <= 60; // Within 1 hour
      },
      isAlternative: true
    },

    // Level 3: Adjacent weekday, same wall clock time
    {
      name: 'adjacent-day',
      filter: (slot) => {
        const slotDate = new Date(slot);
        const dayDiff = Math.abs(getDay(slotDate) - targetDayOfWeek);
        const adjustedDiff = Math.min(dayDiff, 7 - dayDiff); // Handle week wraparound
        return adjustedDiff <= 1 && getHours(slotDate) === targetHour && getMinutes(slotDate) === targetMinute;
      },
      isAlternative: true
    },

    // Level 4: Same weekday, any time
    {
      name: 'same-weekday',
      filter: (slot) => {
        const slotDate = new Date(slot);
        return getDay(slotDate) === targetDayOfWeek;
      },
      isAlternative: true
    },

    // Level 5: Any available slot (fallback)
    {
      name: 'any-available',
      filter: () => true,
      isAlternative: true
    }
  ];

  // STEP 1: Filter by basic constraints first
  const basicFiltered = availableSlots.filter((slot) => {
    const slotDate = new Date(slot);
    const daysSinceFirst = differenceInDays(slotDate, new Date(firstSelection));

    if (usedSlots.has(slot)) {
      return false;
    }

    if (daysSinceFirst < 1) {
      return false;
    }

    return true;
  });

  // STEP 2: Apply monthly quota filtering
  const validSlots = basicFiltered.filter((slot) => {
    const slotDate = new Date(slot);
    const monthKey = format(slotDate, 'yyyy-MM');

    if (!monthlyQuotas) {
      return true;
    }

    const monthData = slotsByMonth[monthKey];

    if (monthData) {
      const currentUsed = monthData.used || 0;
      const firstSelectionMonth = format(new Date(firstSelection), 'yyyy-MM');

      // For the same month as first selection, account for the first selection
      const totalUsedInMonth = monthKey === firstSelectionMonth ? currentUsed + 1 : currentUsed;

      // Apply proper quota limits based on available sessions per month
      // Each month has its own quota based on available + expiring sessions
      if (monthlyQuotas) {
        // For now, use the currentMonth quota as the limit for all months
        // TODO: This should be improved to get proper quota for each specific month
        const monthlyLimit = monthlyQuotas.currentMonth?.total || 2;

        if (totalUsedInMonth >= monthlyLimit) {
          return false;
        }
      }
    }

    return true;
  });

  // STEP 3: Collect ALL possible matches from all preference levels, then sort by proximity
  let allMatches = [];

  for (const pref of preferences) {
    const matches = validSlots.filter(pref.filter);

    // Add preference info to each match
    matches.forEach((match) => {
      allMatches.push({
        timestamp: match,
        isAlternative: pref.isAlternative,
        preferenceLevel: pref.name,
        daysDiff: Math.abs(differenceInDays(new Date(match), targetDate))
      });
    });
  }

  if (allMatches.length > 0) {
    // Smart sorting: balance between ideal spacing and maintaining same time
    allMatches.sort((a, b) => {
      // If dates are on the same day, prefer exact time match
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);

      if (dateA.toDateString() === dateB.toDateString()) {
        // Same day - prioritize exact time match
        if (a.preferenceLevel === 'exact' && b.preferenceLevel !== 'exact') return -1;
        if (b.preferenceLevel === 'exact' && a.preferenceLevel !== 'exact') return 1;
      }

      // For different days, use a weighted scoring system
      // Give bonus points for maintaining same time even if spacing is slightly off
      const SPACING_WEIGHT = 1;
      const TIME_MATCH_BONUS = 3; // Days of tolerance for same time

      // Calculate scores
      let scoreA = a.daysDiff * SPACING_WEIGHT;
      let scoreB = b.daysDiff * SPACING_WEIGHT;

      // Apply bonus for exact time matches (Sunday 10:00 AM)
      if (a.preferenceLevel === 'exact') {
        scoreA -= TIME_MATCH_BONUS; // Lower score is better
      }
      if (b.preferenceLevel === 'exact') {
        scoreB -= TIME_MATCH_BONUS;
      }

      // Compare scores
      if (scoreA !== scoreB) return scoreA - scoreB;

      // If still tied, use original preference order
      const preferenceOrder = ['exact', 'adjacent-time', 'adjacent-day', 'same-weekday', 'any-available'];
      const aIndex = preferenceOrder.indexOf(a.preferenceLevel);
      const bIndex = preferenceOrder.indexOf(b.preferenceLevel);
      if (aIndex !== bIndex) return aIndex - bIndex;

      // Finally, prefer earlier dates
      return dateA - dateB;
    });

    const selected = allMatches[0];

    return {
      timestamp: selected.timestamp,
      isAlternative: selected.isAlternative,
      preferenceLevel: selected.preferenceLevel
    };
  }

  return null;
};
